import { cn } from "@/lib/utils";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Component, ReactNode, useState } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || "";
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("Failed to fetch") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    msg.includes("error loading dynamically imported module") ||
    (error.name === "TypeError" && msg.includes("fetch"))
  );
}

function tryAutoReload(): boolean {
  const key = "chunk-reload-ts";
  const last = sessionStorage.getItem(key);
  if (!last || Date.now() - Number(last) > 30000) {
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
    return true;
  }
  return false;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Auto-reload on chunk load failure BEFORE rendering error UI
    if (isChunkLoadError(error)) {
      tryAutoReload();
    }
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    if (isChunkLoadError(error)) {
      tryAutoReload();
      return;
    }
    // Сохраняем component stack чтобы пользователь мог его скопировать
    this.setState({ componentStack: info.componentStack ?? null });
    // Лог в консоль с полной информацией — для разработчика когда коллега откроет DevTools
    console.error("[ErrorBoundary] Caught error:", error);
    console.error("[ErrorBoundary] Component stack:", info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // If chunk error and we're about to reload, show nothing
      if (isChunkLoadError(this.state.error)) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="w-5 h-5 border-2 border-[#007aff] border-t-transparent rounded-full animate-spin" />
          </div>
        );
      }

      const techInfo = [
        `URL: ${typeof window !== "undefined" ? window.location.href : "—"}`,
        `User-Agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "—"}`,
        ``,
        `Error: ${this.state.error?.message ?? "—"}`,
        ``,
        `Stack:`,
        this.state.error?.stack ?? "—",
        ``,
        `Component stack:`,
        this.state.componentStack ?? "—",
      ].join("\n");

      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-[#f5f5f7]">
          <div className="flex flex-col items-center w-full max-w-sm text-center">
            <img
              src="/cat-broken.jpg"
              alt=""
              className="w-40 h-40 object-contain mb-6"
            />

            <h2 className="text-xl font-semibold text-[#1d1d1f] mb-4 px-2">
              Саша сломал, отправьте ему техническую информацию
            </h2>

            <ArrowDown
              size={22}
              className="text-[#86868b] mb-2 animate-bounce"
            />

            <CopyTechInfoButton text={techInfo} />

            <a
              href="/kanban"
              className={cn(
                "mt-6 flex items-center gap-2 px-5 py-2.5 rounded-xl",
                "bg-[#007aff] text-white text-sm font-medium no-underline",
                "hover:bg-[#0066d6] cursor-pointer transition-colors"
              )}
            >
              Перейти на страницу с вакансиями
              <ArrowRight size={15} />
            </a>

            <details className="mt-8 w-full text-left">
              <summary className="text-xs text-[#86868b] cursor-pointer hover:text-[#1d1d1f]">
                Подробности
              </summary>
              <pre className="mt-2 p-3 rounded-lg bg-white/60 text-[10px] text-[#86868b] overflow-auto max-h-48 whitespace-break-spaces">
                {techInfo}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function CopyTechInfoButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore — fallback ниже не нужен, пользователь увидит pre-блок и скопирует руками
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer",
        copied
          ? "bg-[#34c759] text-white"
          : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
      )}
    >
      {copied ? "Скопировано!" : "Скопировать"}
    </button>
  );
}

export default ErrorBoundary;
